import { Link } from '@tanstack/react-router';
import { GraduationCap, MessageCircle, ShieldCheck, Utensils } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SiteNavbar } from './site/SiteNavbar';
import { Button } from './ui/button';

const PROGRAMS = [
  {
    titleKey: 'landing.programPouponniereTitle',
    ageKey: 'landing.programPouponniereAge',
    bodyKey: 'landing.programPouponniereBody',
  },
  {
    titleKey: 'landing.programToddlerTitle',
    ageKey: 'landing.programToddlerAge',
    bodyKey: 'landing.programToddlerBody',
  },
  {
    titleKey: 'landing.programPreschoolTitle',
    ageKey: 'landing.programPreschoolAge',
    bodyKey: 'landing.programPreschoolBody',
  },
] as const;

const WHY_US = [
  {
    Icon: GraduationCap,
    titleKey: 'landing.whyUsEducatorsTitle',
    bodyKey: 'landing.whyUsEducatorsBody',
  },
  {
    Icon: ShieldCheck,
    titleKey: 'landing.whyUsSafetyTitle',
    bodyKey: 'landing.whyUsSafetyBody',
  },
  {
    Icon: Utensils,
    titleKey: 'landing.whyUsMealsTitle',
    bodyKey: 'landing.whyUsMealsBody',
  },
  {
    Icon: MessageCircle,
    titleKey: 'landing.whyUsCommunicationTitle',
    bodyKey: 'landing.whyUsCommunicationBody',
  },
] as const;

const GALLERY_IMAGES = [
  'https://images.unsplash.com/photo-1544776193-352d25ca82cd?auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1519340241574-2cec6dc318ce?auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1587616211892-b9203b39c5c6?auto=format&fit=crop&w=800&q=80',
] as const;

const TESTIMONIALS = [
  { quoteKey: 'landing.testimonial1Quote', authorKey: 'landing.testimonial1Author' },
  { quoteKey: 'landing.testimonial2Quote', authorKey: 'landing.testimonial2Author' },
] as const;

export default function LandingPage() {
  const { t } = useTranslation();

  return (
    <div className="imaginette-page min-h-screen text-[color:var(--imaginette-ink)]">
      <section className="imaginette-hero-sky relative min-h-[100svh] overflow-hidden">
        <div
          className="imaginette-cloud imaginette-drift -left-16 top-28 h-24 w-44 sm:h-28 sm:w-56"
          aria-hidden
        />
        <div
          className="imaginette-cloud imaginette-drift-slow right-[-3rem] top-40 h-20 w-40 sm:h-24 sm:w-52"
          aria-hidden
        />
        <div
          className="imaginette-cloud imaginette-float bottom-28 left-[12%] h-16 w-36 opacity-70"
          aria-hidden
        />
        <div
          className="imaginette-cloud imaginette-drift bottom-40 right-[18%] h-14 w-28 opacity-60"
          aria-hidden
        />

        <SiteNavbar variant="overlay" />

        <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-5xl flex-col items-center justify-center px-4 pb-16 pt-24 text-center sm:px-6">
          <img
            src="/brand/imaginette-logo.png"
            alt="Garderie Imaginette"
            className="imaginette-float w-[min(82vw,420px)] drop-shadow-[0_18px_40px_rgba(62,198,216,0.28)]"
          />
          <h1 className="mt-8 max-w-2xl font-brand text-xl font-semibold leading-snug text-[color:var(--imaginette-ink)] sm:text-2xl md:text-3xl">
            {t('landing.headline')}
          </h1>
          <p className="mt-3 max-w-xl text-base text-[color:var(--imaginette-ink)]/70 sm:text-lg">
            {t('landing.supporting')}
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button
              asChild
              size="lg"
              className="imaginette-btn-primary rounded-full px-8 text-base font-semibold"
            >
              <Link to="/portal">{t('siteNav.portal')}</Link>
            </Button>
            <Button
              asChild
              size="lg"
              className="imaginette-btn-secondary rounded-full px-8 text-base font-semibold"
            >
              <a href="#contact">{t('landing.ctaContact')}</a>
            </Button>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden px-4 py-20 sm:px-6">
        <span
          className="imaginette-star pointer-events-none absolute left-[8%] top-10 text-3xl"
          aria-hidden
        >
          ★
        </span>
        <span
          className="imaginette-star pointer-events-none absolute right-[12%] top-24 text-2xl"
          aria-hidden
        >
          ★
        </span>
        <div className="relative mx-auto max-w-3xl text-center">
          <h2 className="font-display text-5xl text-[color:var(--imaginette-cyan-deep)] sm:text-6xl">
            {t('landing.aboutTitle')}
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-[color:var(--imaginette-ink)]/75">
            {t('landing.aboutBody')}
          </p>
        </div>
      </section>

      <section className="relative overflow-hidden px-4 py-20 sm:px-6">
        <div
          className="pointer-events-none absolute -left-24 top-0 h-72 w-72 rounded-full bg-[color:var(--imaginette-cyan)]/10 blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-[color:var(--imaginette-green)]/10 blur-3xl"
          aria-hidden
        />
        <div className="relative mx-auto max-w-6xl text-center">
          <h2 className="font-display text-5xl text-[color:var(--imaginette-cyan-deep)] sm:text-6xl">
            {t('landing.programsTitle')}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-[color:var(--imaginette-ink)]/75">
            {t('landing.programsSubtitle')}
          </p>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {PROGRAMS.map((program) => (
              <div
                key={program.titleKey}
                className="rounded-3xl border border-primary-200/60 bg-white/80 p-8 text-left shadow-sm shadow-primary-200/30 backdrop-blur"
              >
                <p className="text-sm font-semibold uppercase tracking-wider text-[color:var(--imaginette-cyan-deep)]">
                  {t(program.ageKey)}
                </p>
                <h3 className="mt-2 font-display text-3xl text-[color:var(--imaginette-ink)]">
                  {t(program.titleKey)}
                </h3>
                <p className="mt-4 text-base leading-relaxed text-[color:var(--imaginette-ink)]/75">
                  {t(program.bodyKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-white/60 px-4 py-20 sm:px-6">
        <div className="relative mx-auto max-w-6xl text-center">
          <h2 className="font-display text-5xl text-[color:var(--imaginette-cyan-deep)] sm:text-6xl">
            {t('landing.whyUsTitle')}
          </h2>
          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {WHY_US.map(({ Icon, titleKey, bodyKey }) => (
              <div key={titleKey} className="flex flex-col items-center rounded-3xl p-6 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[color:var(--imaginette-cyan)]/15 text-[color:var(--imaginette-cyan-deep)]">
                  <Icon className="h-7 w-7" aria-hidden />
                </span>
                <h3 className="mt-4 font-brand text-lg font-semibold text-[color:var(--imaginette-ink)]">
                  {t(titleKey)}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-[color:var(--imaginette-ink)]/70">
                  {t(bodyKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden px-4 py-20 sm:px-6">
        <div className="relative mx-auto max-w-6xl text-center">
          <h2 className="font-display text-5xl text-[color:var(--imaginette-cyan-deep)] sm:text-6xl">
            {t('landing.galleryTitle')}
          </h2>
          {/*
            Placeholder stock photos (Unsplash) illustrating daycare life.
            Replace with real photos of Garderie Imaginette's centre once available.
          */}
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {GALLERY_IMAGES.map((src) => (
              <div
                key={src}
                className="aspect-square overflow-hidden rounded-3xl border border-primary-200/60 shadow-sm shadow-primary-200/30"
              >
                <img src={src} alt="" className="h-full w-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-white/60 px-4 py-20 sm:px-6">
        <div className="relative mx-auto max-w-5xl text-center">
          <h2 className="font-display text-5xl text-[color:var(--imaginette-cyan-deep)] sm:text-6xl">
            {t('landing.testimonialsTitle')}
          </h2>
          {/*
            Placeholder testimonials. Replace with real quotes gathered from
            Garderie Imaginette parents once available.
          */}
          <div className="mt-12 grid gap-8 md:grid-cols-2">
            {TESTIMONIALS.map(({ quoteKey, authorKey }) => (
              <blockquote
                key={quoteKey}
                className="rounded-3xl border border-primary-200/60 bg-white/80 p-8 text-left shadow-sm shadow-primary-200/30"
              >
                <span className="imaginette-star text-3xl" aria-hidden>
                  “
                </span>
                <p className="mt-2 text-lg leading-relaxed text-[color:var(--imaginette-ink)]/80">
                  {t(quoteKey)}
                </p>
                <footer className="mt-4 text-sm font-semibold uppercase tracking-wider text-[color:var(--imaginette-cyan-deep)]">
                  — {t(authorKey)}
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      <section
        id="contact"
        className="relative overflow-hidden px-4 py-20 sm:px-6"
        style={{
          background:
            'linear-gradient(160deg, #3ec6d8 0%, #2eb3c9 45%, #8fd14a 130%)',
        }}
      >
        <div
          className="imaginette-cloud imaginette-drift -right-10 top-8 h-20 w-44 opacity-40"
          aria-hidden
        />
        <div
          className="imaginette-cloud imaginette-drift-slow -left-8 bottom-6 h-16 w-36 opacity-35"
          aria-hidden
        />
        <div className="relative mx-auto grid max-w-6xl gap-12 text-white md:grid-cols-[1.2fr_1fr]">
          <div>
            <h2 className="font-display text-5xl sm:text-6xl">{t('landing.contactTitle')}</h2>
            <p className="mt-4 max-w-md text-lg text-white/90">{t('landing.contactBody')}</p>
            <a
              href="https://www.facebook.com/p/Garderie-Imaginette-100082967577078/"
              target="_blank"
              rel="noreferrer"
              className="mt-6 inline-flex items-center gap-2 font-semibold text-[color:var(--imaginette-star)] underline-offset-4 hover:underline"
            >
              <span aria-hidden>★</span>
              {t('landing.facebook')}
            </a>
          </div>
          <div className="space-y-6 text-base sm:text-lg">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-white/70">
                {t('landing.addressLabel')}
              </p>
              <p className="mt-1">
                23 boul. des Ormes
                <br />
                Saint-Basile-le-Grand (QC) J3N 1G4
              </p>
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-white/70">
                {t('landing.phoneLabel')}
              </p>
              <a href="tel:+14504611632" className="mt-1 inline-block hover:text-[color:var(--imaginette-star)]">
                (450) 461-1632
              </a>
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-white/70">
                {t('landing.hoursLabel')}
              </p>
              <p className="mt-1">{t('landing.hoursValue')}</p>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-primary-200/60 px-4 py-8 text-center text-sm text-[color:var(--imaginette-ink)]/55 sm:px-6">
        <img
          src="/brand/imaginette-logo.png"
          alt=""
          className="mx-auto mb-3 h-14 w-auto opacity-90"
        />
        © {new Date().getFullYear()} Garderie Imaginette
      </footer>
    </div>
  );
}
